import * as THREE from 'three';

export type MacroProfile = 'linear' | 'ripple' | 'donut' | 'bottle' | 'accordion' | 'concave';
export type SurfaceTexture = 'none' | 'knurling' | 'diamond' | 'voronoi' | 'spiral_ribs' | 'triangles';
export type SocketStandard = 'e27' | 'e14' | 'custom';
export type PrintMode = 'vase' | 'solid';

export interface SuperLampParams {
  // 1. Silueta Macro
  profile: MacroProfile;
  height: number;               // mm (ej. 170)
  bottomDiameter: number;       // mm (ej. 130)
  topDiameter: number;          // mm (ej. 85)
  verticalSections: number;     // Secciones abultadas tipo Ripple / Accordion (1 a 6)
  macroBulgeAmplitude: number;  // Amplitud del pliegue/abultamiento vertical en mm (ej. 15)
  polygonSides: number;         // Número de lados transversales (3 a 64, donde 64 = círculo suave)

  // 2. Torsión & Facetado
  twistAngle: number;           // Torsión angular en grados (-180 a +180)
  isLowPoly: boolean;           // Flat Shading con normales afiladas por faceta
  staggerVertices: boolean;     // Desplazamiento alternado en filas pares (genera triángulos romboidales)

  // 3. Capa de Textura 3D Volumétrica
  texture: SurfaceTexture;
  textureScaleX: number;        // Frecuencia horizontal / perimetral (ej. 12 a 60)
  textureScaleY: number;        // Frecuencia vertical (ej. 8 a 40)
  textureDepth: number;         // Profundidad de relieve 3D real en mm (0 a 10)

  // 4. Modos de Impresión y Portalámparas
  printMode: PrintMode;         // 'vase' (superficie de 1 pared) o 'solid' (con espesor hacia adentro)
  wallThickness: number;        // Espesor de pared en mm para modo sólido (ej. 1.6 o 2.0)
  hasSocketHole: boolean;       // Orificio en la base
  socketType: SocketStandard;   // E27, E14 o Custom
  customSocketDiameter: number; // mm si socketType es 'custom'
  socketLipHeight: number;      // Altura del cuello/labio del portalámpara en mm (ej. 4)

  // Calidad de malla
  segmentsHeight?: number;      // Resolución vertical
  segmentsRadial?: number;      // Resolución radial
}

export function getSocketHoleDiameter(type: SocketStandard, customDiameter: number): number {
  switch (type) {
    case 'e27': return 41.5;
    case 'e14': return 28.5;
    case 'custom': return customDiameter || 40.0;
  }
}

/**
 * Super Generador Paramétrico de Pantallas de Lámparas
 * Capaz de desacoplar Silueta Macro de Micro-Textura volumétrica y Low-Poly
 */
export function generateSuperLampGeometry(params: SuperLampParams): THREE.BufferGeometry {
  const {
    profile = 'ripple',
    height = 170,
    bottomDiameter = 130,
    topDiameter = 85,
    verticalSections = 3,
    macroBulgeAmplitude = 12,
    polygonSides = 48,
    twistAngle = 0,
    isLowPoly = false,
    staggerVertices = false,
    texture = 'none',
    textureScaleX = 24,
    textureScaleY = 16,
    textureDepth = 3.5,
    printMode = 'vase',
    wallThickness = 1.6,
    hasSocketHole = true,
    socketType = 'e27',
    customSocketDiameter = 40,
    socketLipHeight = 0
  } = params;

  // Ajuste de resolución dinámica
  let radialSegs = isLowPoly ? Math.max(3, Math.min(polygonSides, 24)) : Math.max(polygonSides, 60);
  let heightSegs = isLowPoly ? Math.max(6, Math.min(verticalSections * 6, 30)) : 80;

  const bottomRadius = bottomDiameter / 2;
  const topRadius = topDiameter / 2;
  const socketRadius = getSocketHoleDiameter(socketType, customSocketDiameter) / 2;
  const twistRad = (twistAngle * Math.PI) / 180;

  // Función que calcula el radio base de la Silueta Macro según altura normalizada v [0, 1]
  const calculateMacroRadius = (v: number): number => {
    let r = THREE.MathUtils.lerp(bottomRadius, topRadius, v);

    switch (profile) {
      case 'ripple':
        // Múltiples pliegues / secciones abultadas (Table Lamp 2.0 / Ripple Lamp)
        r += Math.sin(v * Math.PI * verticalSections * 2) * macroBulgeAmplitude;
        break;

      case 'donut':
        // Abultamiento central máximo tipo globo / toroide
        r += Math.sin(v * Math.PI) * (macroBulgeAmplitude * 2.0);
        break;

      case 'accordion':
        // Pliegues triangulares / zig-zag en altura
        const phase = (v * verticalSections) % 1;
        const triWave = (phase < 0.5 ? phase * 2 : (1 - phase) * 2) - 0.5;
        r += triWave * macroBulgeAmplitude * 2;
        break;

      case 'bottle':
        // Base ancha, cuello esbelto tipo botella nórdica
        const smoothStep = v < 0.6 ? 0 : Math.pow((v - 0.6) / 0.4, 2);
        r = THREE.MathUtils.lerp(bottomRadius, topRadius, smoothStep);
        break;

      case 'concave':
        // Cintura estrecha tipo reloj de arena
        r -= Math.sin(v * Math.PI) * macroBulgeAmplitude;
        break;

      case 'linear':
      default:
        break;
    }

    return Math.max(5, r);
  };

  // Función que calcula la textura y desplazamiento 3D real en mm
  const calculateTextureDisplacement = (u: number, v: number): number => {
    if (texture === 'none' || textureDepth <= 0) return 0;

    switch (texture) {
      case 'knurling':
        // Micro-puntos / Moleteado esférico (Knurling / Diamond Grip)
        const dotX = Math.sin(u * Math.PI * 2 * textureScaleX);
        const dotY = Math.sin(v * Math.PI * 2 * textureScaleY);
        return Math.max(0, dotX * dotY) * textureDepth;

      case 'diamond':
        // Malla de Diamantes y Rombos salientes/entrantes
        const dX = Math.cos(u * Math.PI * 2 * textureScaleX + v * Math.PI * 2 * textureScaleY);
        const dY = Math.cos(u * Math.PI * 2 * textureScaleX - v * Math.PI * 2 * textureScaleY);
        return (dX + dY) * 0.5 * textureDepth;

      case 'triangles':
        // Triángulos desplazados en relieve
        const tVal = Math.sin((u * textureScaleX + v * textureScaleY) * Math.PI * 2);
        return Math.abs(tVal) * textureDepth;

      case 'spiral_ribs':
        // Costillas en espiral continua
        return Math.sin((u * textureScaleX + v * 3) * Math.PI * 2) * textureDepth;

      case 'voronoi':
        // Textura celular pseudo-voronoi
        const c1 = Math.sin(u * Math.PI * textureScaleX) * Math.cos(v * Math.PI * textureScaleY);
        const c2 = Math.cos(u * Math.PI * (textureScaleX * 1.5)) * Math.sin(v * Math.PI * (textureScaleY * 1.5));
        return (c1 + c2) * 0.5 * textureDepth;

      default:
        return 0;
    }
  };

  const positions: number[] = [];
  const indices: number[] = [];

  // 1. GENERACIÓN DE SUPERFICIE EXTERIOR
  for (let y = 0; y <= heightSegs; y++) {
    const v = y / heightSegs;
    const currentY = v * height;
    const baseR = calculateMacroRadius(v);
    const currentTwist = v * twistRad;

    // Desfase alternado de vértices para patrón triangular low-poly (Staggered Grid)
    const staggerOffset = (staggerVertices && y % 2 === 1) ? (0.5 / radialSegs) * Math.PI * 2 : 0;

    for (let x = 0; x <= radialSegs; x++) {
      const u = x / radialSegs;
      const angle = u * Math.PI * 2 + currentTwist + staggerOffset;

      const displacement = calculateTextureDisplacement(u, v);
      const totalR = Math.max(5, baseR + displacement);

      const px = Math.cos(angle) * totalR;
      const pz = Math.sin(angle) * totalR;

      positions.push(px, currentY, pz);
    }
  }

  // Índices de la superficie exterior
  const rowStride = radialSegs + 1;
  for (let y = 0; y < heightSegs; y++) {
    for (let x = 0; x < radialSegs; x++) {
      const a = y * rowStride + x;
      const b = (y + 1) * rowStride + x;
      const c = (y + 1) * rowStride + (x + 1);
      const d = y * rowStride + (x + 1);

      indices.push(a, b, d);
      indices.push(b, c, d);
    }
  }

  // 2. ORIFICIO / ENCASTRE INFERIOR PARA PORTALÁMPARAS (E27 / E14 / Custom)
  if (hasSocketHole && bottomRadius > socketRadius + 3) {
    const socketStartIdx = positions.length / 3;
    const socketLipY = Math.max(0, socketLipHeight);

    // Anillo interior del portalámparas
    for (let x = 0; x <= radialSegs; x++) {
      const u = x / radialSegs;
      const angle = u * Math.PI * 2;
      positions.push(Math.cos(angle) * socketRadius, socketLipY, Math.sin(angle) * socketRadius);
    }

    // Unir orificio interior con el borde exterior de la base
    // Orientación manifold consistente (normales hacia abajo en la base)
    for (let x = 0; x < radialSegs; x++) {
      const outerA = x;
      const outerB = x + 1;
      const innerA = socketStartIdx + x;
      const innerB = socketStartIdx + x + 1;

      indices.push(outerA, outerB, innerA);
      indices.push(innerA, outerB, innerB);
    }
  }

  // 3. MODO SÓLIDO CON ESPESOR DE PARED (Shell / Inward Extrude)
  if (printMode === 'solid' && wallThickness > 0.5) {
    const innerStartIdx = positions.length / 3;

    // Duplicar pared extruida hacia adentro según wallThickness
    for (let y = 0; y <= heightSegs; y++) {
      const v = y / heightSegs;
      const currentY = v * height;
      const baseR = calculateMacroRadius(v);
      const currentTwist = v * twistRad;
      const staggerOffset = (staggerVertices && y % 2 === 1) ? (0.5 / radialSegs) * Math.PI * 2 : 0;

      for (let x = 0; x <= radialSegs; x++) {
        const u = x / radialSegs;
        const angle = u * Math.PI * 2 + currentTwist + staggerOffset;

        const displacement = calculateTextureDisplacement(u, v);
        const innerR = Math.max(3, baseR + displacement - wallThickness);

        const px = Math.cos(angle) * innerR;
        const pz = Math.sin(angle) * innerR;

        positions.push(px, currentY, pz);
      }
    }

    // Caras de la pared interior (normales invertidas hacia el centro)
    for (let y = 0; y < heightSegs; y++) {
      for (let x = 0; x < radialSegs; x++) {
        const a = innerStartIdx + y * rowStride + x;
        const b = innerStartIdx + (y + 1) * rowStride + x;
        const c = innerStartIdx + (y + 1) * rowStride + (x + 1);
        const d = innerStartIdx + y * rowStride + (x + 1);

        indices.push(a, d, b);
        indices.push(b, d, c);
      }
    }

    // Tapa / Borde superior uniendo pared exterior con pared interior
    const topOuterRowStart = heightSegs * rowStride;
    const topInnerRowStart = innerStartIdx + heightSegs * rowStride;

    for (let x = 0; x < radialSegs; x++) {
      const outA = topOuterRowStart + x;
      const outB = topOuterRowStart + x + 1;
      const inA = topInnerRowStart + x;
      const inB = topInnerRowStart + x + 1;

      indices.push(outA, inA, outB);
      indices.push(outB, inA, inB);
    }

    // Si no tiene orificio de encastre, cerrar el fondo entre pared exterior e interior
    if (!hasSocketHole) {
      for (let x = 0; x < radialSegs; x++) {
        const outA = x;
        const outB = x + 1;
        const inA = innerStartIdx + x;
        const inB = innerStartIdx + x + 1;

        indices.push(outA, outB, inA);
        indices.push(inA, outB, inB);
      }
    }
  }

  let geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);

  // 4. LOW-POLY / FLAT SHADING: Desasociar índices para caras con normales totalmente independientes
  if (isLowPoly) {
    geometry = geometry.toNonIndexed();
    geometry.computeVertexNormals();
  } else {
    geometry.computeVertexNormals();
  }

  return geometry;
}

/**
 * Exportador directo a formato binario STL estándar para Slicers 3D
 */
export function exportSuperLampToSTL(geometry: THREE.BufferGeometry, filename = 'lampara-mila-super.stl') {
  let geom = geometry.clone();
  if (geom.index) {
    geom = geom.toNonIndexed();
  }

  const posAttr = geom.getAttribute('position');
  const trianglesCount = posAttr.count / 3;

  const bufferLength = 80 + 4 + trianglesCount * 50;
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const dataView = new DataView(arrayBuffer);

  // Cabecera STL
  const header = 'MilaOS 3D Generator - Parametric Lampshade STL';
  for (let i = 0; i < 80; i++) {
    dataView.setUint8(i, i < header.length ? header.charCodeAt(i) : 0);
  }

  dataView.setUint32(80, trianglesCount, true);

  let offset = 84;
  const pA = new THREE.Vector3();
  const pB = new THREE.Vector3();
  const pC = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const cb = new THREE.Vector3();
  const ab = new THREE.Vector3();

  for (let i = 0; i < posAttr.count; i += 3) {
    pA.fromBufferAttribute(posAttr, i);
    pB.fromBufferAttribute(posAttr, i + 1);
    pC.fromBufferAttribute(posAttr, i + 2);

    cb.subVectors(pC, pB);
    ab.subVectors(pA, pB);
    cb.cross(ab).normalize();
    normal.copy(cb);

    // Normales
    dataView.setFloat32(offset, normal.x, true);
    dataView.setFloat32(offset + 4, normal.y, true);
    dataView.setFloat32(offset + 8, normal.z, true);
    offset += 12;

    // Vértices A, B, C
    dataView.setFloat32(offset, pA.x, true);
    dataView.setFloat32(offset + 4, pA.y, true);
    dataView.setFloat32(offset + 8, pA.z, true);
    offset += 12;

    dataView.setFloat32(offset, pB.x, true);
    dataView.setFloat32(offset + 4, pB.y, true);
    dataView.setFloat32(offset + 8, pB.z, true);
    offset += 12;

    dataView.setFloat32(offset, pC.x, true);
    dataView.setFloat32(offset + 4, pC.y, true);
    dataView.setFloat32(offset + 8, pC.z, true);
    offset += 12;

    dataView.setUint16(offset, 0, true);
    offset += 2;
  }

  const blob = new Blob([arrayBuffer], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename.endsWith('.stl') ? filename : `${filename}.stl`;
  link.click();
  URL.revokeObjectURL(link.href);
}
